import { useMemo } from "react";
import { motion } from "framer-motion";
import { M } from "../../data/site";

const EASE = [0.16, 1, 0.3, 1];
const MARK_ASPECT = "960 / 550";
const NAME_ASPECT = "1080 / 120";

const Particles = ({ s, count }) => {
  const dots = useMemo(() => Array.from({ length: count }, (_, i) => {
    const a = (i / count) * Math.PI * 2 + Math.random() * 0.4;
    const r = 260 + Math.random() * 300;
    return { x: Math.cos(a) * r, y: Math.sin(a) * r, d: Math.random() * 0.6, size: 2 + Math.random() * 3 };
  }), [count]);
  return dots.map((p, i) => (
    <motion.span key={i} className="absolute left-1/2 top-1/2 z-10 rounded-full bg-cyan-200 shadow-[0_0_12px_#22d3ee]" style={{ width: p.size, height: p.size }}
      initial={{ x: p.x, y: p.y, opacity: 0 }} animate={{ x: 0, y: 0, opacity: [0, 1, 1, 0] }} transition={{ duration: 1.6 * s, delay: p.d * s, ease: [0.7, 0, 0.3, 1] }} />
  ));
};

const SlicedMark = ({ s, slices }) => {
  const parts = useMemo(() => Array.from({ length: slices }, (_, i) => ({
    dx: (Math.random() - 0.5) * 320, d: 0.35 + Math.random() * 0.9, i,
  })), [slices]);
  return (
    <div className="relative w-full" style={{ aspectRatio: MARK_ASPECT }}>
      {parts.map(({ dx, d, i }) => (
        <motion.img key={i} src={M("logo_mark.webp")} alt="" draggable={false}
          className="absolute inset-0 h-full w-full select-none mix-blend-screen"
          style={{ clipPath: `inset(${(i * 100) / slices}% 0 ${100 - ((i + 1) * 100) / slices - 0.3}% 0)` }}
          initial={{ x: dx, opacity: 0, filter: "brightness(3.5) saturate(0.4)" }}
          animate={{ x: [dx, -dx * 0.12, 0], opacity: [0, 1, 0.35, 1], filter: ["brightness(3.5) saturate(0.4)", "brightness(1.8) saturate(1.2)", "brightness(1) saturate(1)"] }}
          transition={{ duration: 1 * s, delay: d * s, ease: EASE }} />
      ))}
      {[["-7px", "hue-rotate(-40deg)"], ["7px", "hue-rotate(60deg)"]].map(([x, f]) => (
        <motion.img key={x} src={M("logo_mark.webp")} alt="" draggable={false} className="pointer-events-none absolute inset-0 h-full w-full select-none mix-blend-screen"
          style={{ x, filter: `${f} brightness(1.4)` }} initial={{ opacity: 0 }} animate={{ opacity: [0, 0.7, 0, 0.5, 0] }} transition={{ duration: 0.55 * s, delay: 2.15 * s }} />
      ))}
      <motion.div className="pointer-events-none absolute inset-y-0 w-[3px] bg-cyan-100 shadow-[0_0_24px_8px_rgba(34,211,238,0.85)]"
        initial={{ left: "0%", opacity: 0 }} animate={{ left: ["0%", "100%"], opacity: [0, 1, 1, 0] }} transition={{ duration: 1.6 * s, delay: 0.45 * s, ease: "easeInOut" }} />
    </div>
  );
};

const WipeImage = ({ src, aspect, s, delay, className = "" }) => (
  <div className={`relative w-full ${className}`} style={{ aspectRatio: aspect }}>
    <motion.img src={src} alt="" draggable={false} className="absolute inset-0 h-full w-full select-none mix-blend-screen"
      initial={{ clipPath: "inset(0 100% 0 0)", opacity: 0.4 }} animate={{ clipPath: "inset(0 0% 0 0)", opacity: [0.4, 1, 0.6, 1] }}
      transition={{ duration: 1.1 * s, delay: delay * s, ease: "easeInOut" }} />
    <motion.div className="pointer-events-none absolute inset-y-[-20%] w-[2px] bg-white shadow-[0_0_18px_6px_rgba(56,189,248,0.9)]"
      initial={{ left: "0%", opacity: 0 }} animate={{ left: ["0%", "100%"], opacity: [0, 1, 1, 0] }} transition={{ duration: 1.1 * s, delay: delay * s, ease: "easeInOut" }} />
  </div>
);

export const LogoBuild = ({ s = 1, compact = false, testId = "intro-logo-build" }) => (
  <div data-testid={testId} className="absolute inset-0 flex flex-col items-center justify-center overflow-hidden bg-black px-6">
    <motion.div className="pointer-events-none absolute h-[620px] w-[620px] rounded-full bg-blue-600/25 blur-[130px]" initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: [0, 0.5, 1], scale: 1 }} transition={{ duration: 3 * s, delay: 0.3 * s }} />
    <div className={`relative grid w-full place-items-center ${compact ? "max-w-[380px]" : "max-w-[680px]"}`}>
      <Particles s={s} count={compact ? 18 : 36} />
      <motion.div className="w-full" animate={{ scale: [1, 1, 1.04, 1], filter: ["drop-shadow(0 0 0px #22d3ee)", "drop-shadow(0 0 0px #22d3ee)", "drop-shadow(0 0 36px #38bdf8)", "drop-shadow(0 0 14px #22d3ee)"] }} transition={{ duration: 4.6 * s, times: [0, 0.78, 0.87, 1] }}>
        <SlicedMark s={s} slices={compact ? 9 : 16} />
      </motion.div>
    </div>
    <div className={`relative mt-2 w-full ${compact ? "max-w-[340px]" : "max-w-[720px]"}`}>
      <WipeImage src={M("logo_name.webp")} aspect={NAME_ASPECT} s={s} delay={2.55} />
      {!compact && (
        <motion.img src={M("logo_tag.webp")} alt="One companion. Endless possibilities." className="mx-auto mt-4 w-[88%] mix-blend-screen" style={{ aspectRatio: "1020 / 47" }}
          initial={{ opacity: 0, filter: "blur(8px)", scale: 1.08 }} animate={{ opacity: 1, filter: "blur(0px)", scale: 1 }} transition={{ duration: 1.1 * s, delay: 3.6 * s, ease: EASE }} />
      )}
    </div>
  </div>
);
